import React, { useState, useRef, useEffect, useMemo } from 'react';
import { ChevronLeft, ChevronRight, Calendar, X } from 'lucide-react';
import { useTheme } from '../context/ThemeContext';

const MONTHS = [
    'Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun',
    'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'
];

/**
 * A custom Month-Year picker that matches the dark premium aesthetic.
 * 
 * Props:
 * - value: string 'YYYY-MM' or ''
 * - onChange: function (newValue 'YYYY-MM')
 * - className: string
 * - style: object
 * - minMonth: string 'YYYY-MM' (optional)
 * - maxMonth: string 'YYYY-MM' (optional)
 * - disabled: boolean
 * - placeholder: string
 */
const MonthYearPicker = ({
    value,
    onChange,
    className = '',
    style = {},
    minMonth = '1960-01',
    maxMonth = `${new Date().getFullYear() + 10}-12`,
    disabled = false,
    placeholder = 'Select Month'
}) => {
    const { isDark } = useTheme();
    const [isOpen, setIsOpen] = useState(false);
    const containerRef = useRef(null);

    // Parse current value or use today
    const parsed = useMemo(() => {
        if (!value || !/^\d{4}-\d{2}$/.test(value)) return null;
        const [y, m] = value.split('-').map(Number);
        return { year: y, month: m - 1 }; // 0-indexed month
    }, [value]);

    // Internal state for the view (what year we are looking at in the picker)
    const [viewYear, setViewYear] = useState(parsed?.year || new Date().getFullYear());

    // Update viewYear when external value changes
    useEffect(() => {
        if (parsed) setViewYear(parsed.year);
    }, [parsed]);

    // Handle click outside to close
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (containerRef.current && !containerRef.current.contains(event.target)) {
                setIsOpen(false);
            }
        };
        if (isOpen) {
            document.addEventListener('mousedown', handleClickOutside);
        }
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, [isOpen]);

    const handleMonthSelect = (monthIndex) => {
        if (disabled) return;
        const monthStr = (monthIndex + 1).toString().padStart(2, '0');
        const newValue = `${viewYear}-${monthStr}`;

        // Check constraints before calling onChange
        if (newValue < minMonth || newValue > maxMonth) return;

        onChange(newValue);
        setIsOpen(false);
    };

    const handlePrevYear = (e) => {
        e.stopPropagation();
        setViewYear(v => v - 1);
    };

    const handleNextYear = (e) => {
        e.stopPropagation();
        setViewYear(v => v + 1);
    };

    const displayValue = useMemo(() => {
        if (!parsed) return '';
        return `${MONTHS[parsed.month]} ${parsed.year}`;
    }, [parsed]);

    // Check if a specific month in the current viewYear is enabled
    const isMonthEnabled = (monthIndex) => {
        const mStr = (monthIndex + 1).toString().padStart(2, '0');
        const checkVal = `${viewYear}-${mStr}`;
        return checkVal >= minMonth && checkVal <= maxMonth;
    };

    return (
        <div className={`relative ${className}`} style={{ ...style, position: 'relative' }} ref={containerRef}>
            <div
                onClick={() => !disabled && setIsOpen(!isOpen)}
                className={`flex items-center justify-between cursor-pointer w-full h-full transition-all ${disabled ? 'opacity-50 cursor-not-allowed' : ''}`}
            >
                <span className={!displayValue ? 'opacity-40' : ''}>
                    {displayValue || placeholder}
                </span>
                <Calendar className="w-4 h-4 opacity-40" />
            </div>
            {isOpen && (
                <div
                    className="absolute left-0 right-0 top-full mt-2 z-[100] rounded-xl shadow-2xl p-4 animate-in fade-in zoom-in duration-150"
                    style={{
                        background: isDark ? '#0d1019' : '#ffffff',
                        border: `1px solid ${isDark ? 'rgba(255,255,255,0.1)' : '#e2e8f0'}`,
                        minWidth: '220px',
                        boxShadow: isDark ? '0 20px 50px rgba(0,0,0,0.6)' : '0 8px 32px rgba(59,130,246,0.12)'
                    }}
                    onMouseDown={e => e.stopPropagation()}
                >
                    {/* Header */}
                    <div className="flex items-center justify-between mb-4 px-1">
                        <button
                            type="button"
                            onClick={handlePrevYear}
                            className={`p-1 rounded transition-colors ${isDark ? 'hover:bg-white/5' : 'hover:bg-gray-100'}`}
                            disabled={viewYear <= Number(minMonth.split('-')[0])}
                        >
                            <ChevronLeft className={`w-4 h-4 ${isDark ? 'opacity-60' : 'text-gray-400'}`} />
                        </button>
                        <span className={`text-sm font-bold ${isDark ? 'text-white/90' : 'text-gray-700'}`}>{viewYear}</span>
                        <button
                            type="button"
                            onClick={handleNextYear}
                            className={`p-1 rounded transition-colors ${isDark ? 'hover:bg-white/5' : 'hover:bg-gray-100'}`}
                            disabled={viewYear >= Number(maxMonth.split('-')[0])}
                        >
                            <ChevronRight className={`w-4 h-4 ${isDark ? 'opacity-60' : 'text-gray-400'}`} />
                        </button>
                    </div>

                    {/* Grid */}
                    <div className="grid grid-cols-3 gap-1">
                        {MONTHS.map((m, idx) => {
                            const enabled = isMonthEnabled(idx);
                            const isSelected = parsed && parsed.year === viewYear && parsed.month === idx;

                            return (
                                <button
                                    key={m}
                                    type="button"
                                    disabled={!enabled}
                                    onClick={() => handleMonthSelect(idx)}
                                    className={`py-2 text-xs font-medium rounded-lg transition-all ${isSelected
                                            ? (isDark ? 'bg-amber-500 text-amber-950 font-bold' : 'bg-blue-600 text-white font-bold')
                                            : enabled
                                                ? (isDark ? 'text-white/70 hover:bg-white/5 hover:text-white' : 'text-gray-600 hover:bg-gray-50 hover:text-blue-600')
                                                : (isDark ? 'text-white/10 cursor-not-allowed' : 'text-gray-200 cursor-not-allowed')
                                        }`}
                                >
                                    {m}
                                </button>
                            );
                        })}
                    </div>

                    {/* Footer / Clear if needed */}
                    {value && (
                        <div className={`mt-4 pt-3 border-t flex justify-center ${isDark ? 'border-white/5' : 'border-gray-100'}`}>
                            <button
                                type="button"
                                onClick={(e) => { e.stopPropagation(); onChange(''); setIsOpen(false); }}
                                className={`text-[10px] uppercase tracking-wider font-bold transition-colors ${
                                    isDark ? 'text-white/30 hover:text-red-400' : 'text-gray-400 hover:text-red-500'
                                }`}
                            >
                                Clear Selection
                            </button>
                        </div>
                    )}
                </div>
            )}
        </div>
    );
};

export default MonthYearPicker;
